import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axiosClient from '../../../components/api/axiosClient';
import LoadContentSpinner from '../../../components/Utils/LoadContentSpinner';
import { useError } from '../../../components/contexts/ErrorContext';
import PassSessionTabs from '../../../components/Dashboard/PassSessionTabs';
import SessionBadge from '../../../components/Dashboard/SessionBadge';
import {
    isMultiSessionEvent,
    resolveInitialTicketTab,
    ticketsForTab,
} from '../../../utils/sessionTickets';

const MIN_PRICE = 500;

function formatXof(value) {
    if (value === '' || value === null || value === undefined || Number.isNaN(Number(value))) {
        return '—';
    }
    return `${Number(value).toLocaleString('fr-FR')} XOF`;
}

function formatDateTime(value) {
    if (!value) return 'Immédiat / sans limite';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function calcDiscount(catalog, promo) {
    const base = Number(catalog);
    const next = Number(promo);
    if (!base || !next || Number.isNaN(base) || Number.isNaN(next) || next >= base) {
        return null;
    }
    const amount = base - next;
    const percent = Math.round((amount / base) * 100);
    return { amount, percent };
}

export default function PromotionEventDetail() {
    const { id } = useParams();
    const { handleError } = useError();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingPivotId, setSavingPivotId] = useState(null);
    const [forms, setForms] = useState({});
    const [selectedPivotId, setSelectedPivotId] = useState(null);
    const [activeTicketTab, setActiveTicketTab] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        axiosClient
            .get(`/dashboard/promotions/events/${id}`)
            .then((response) => {
                const data = response.data.data;
                setEvent(data);
                const next = {};
                (data.tickets || []).forEach((ticket) => {
                    next[ticket.pivot_id] = {
                        promo_price: ticket.promotion?.promo_price ?? '',
                        starts_at: ticket.promotion?.starts_at
                            ? ticket.promotion.starts_at.slice(0, 16)
                            : '',
                        ends_at: ticket.promotion?.ends_at
                            ? ticket.promotion.ends_at.slice(0, 16)
                            : '',
                    };
                });
                setForms(next);
                setSelectedPivotId((prev) => {
                    if (prev && data.tickets?.some((t) => t.pivot_id === prev)) return prev;
                    return data.tickets?.[0]?.pivot_id ?? null;
                });
            })
            .catch((error) => {
                handleError(error?.response?.data?.message || 'Impossible de charger cet événement.');
            })
            .finally(() => setLoading(false));
    }, [id, handleError]);

    useEffect(() => {
        load();
    }, [load]);

    const isMultiSession = isMultiSessionEvent(event);
    const allTickets = event?.tickets || [];

    useEffect(() => {
        if (!event || !isMultiSession) {
            setActiveTicketTab(null);
            return;
        }
        setActiveTicketTab(resolveInitialTicketTab(event, allTickets));
    }, [event?.id, event?.is_multi_session, allTickets.length]);

    const visibleTickets = useMemo(() => {
        if (!isMultiSession || !activeTicketTab) return allTickets;
        return ticketsForTab(allTickets, activeTicketTab, event);
    }, [allTickets, isMultiSession, activeTicketTab, event]);

    const updateForm = (pivotId, field, value) => {
        setSelectedPivotId(pivotId);
        setForms((prev) => ({
            ...prev,
            [pivotId]: { ...prev[pivotId], [field]: value },
        }));
    };

    const selectedTicket = useMemo(
        () => (event?.tickets || []).find((t) => t.pivot_id === selectedPivotId) || null,
        [event, selectedPivotId]
    );

    const selectedForm = selectedTicket ? forms[selectedTicket.pivot_id] || {} : {};

    const preview = useMemo(() => {
        if (!selectedTicket) return null;
        const catalog = selectedTicket.prix_unitaire;
        const draftPromo = selectedForm.promo_price !== '' ? Number(selectedForm.promo_price) : null;
        const livePromo = selectedTicket.promotion?.is_currently_active
            ? selectedTicket.promotion.promo_price
            : null;
        const displayPromo = draftPromo && !Number.isNaN(draftPromo) ? draftPromo : livePromo;
        const discount = displayPromo != null ? calcDiscount(catalog, displayPromo) : null;
        const validDraft =
            draftPromo &&
            !Number.isNaN(draftPromo) &&
            draftPromo >= MIN_PRICE &&
            draftPromo < catalog;

        return {
            catalog,
            displayPromo,
            discount,
            validDraft: Boolean(validDraft),
            starts_at: selectedForm.starts_at || selectedTicket.promotion?.starts_at,
            ends_at: selectedForm.ends_at || selectedTicket.promotion?.ends_at,
            isActive: Boolean(selectedTicket.promotion?.is_currently_active),
            stock: selectedTicket.nbr_ticket,
            name: selectedTicket.name,
        };
    }, [selectedTicket, selectedForm]);

    const eventSummary = useMemo(() => {
        const tickets = event?.tickets || [];
        const active = tickets.filter((t) => t.promotion?.is_currently_active);
        const totalSavings = active.reduce((sum, t) => {
            const d = calcDiscount(t.prix_unitaire, t.promotion.promo_price);
            return sum + (d?.amount || 0);
        }, 0);
        return {
            total: tickets.length,
            activeCount: active.length,
            totalSavings,
        };
    }, [event]);

    const activatePromo = async (ticket) => {
        const form = forms[ticket.pivot_id] || {};
        const promoPrice = parseInt(form.promo_price, 10);

        if (!promoPrice || Number.isNaN(promoPrice)) {
            handleError('Indiquez un prix promotionnel valide.');
            return;
        }
        if (promoPrice < MIN_PRICE) {
            handleError(`Le prix promo doit être d'au moins ${MIN_PRICE} XOF.`);
            return;
        }
        if (promoPrice >= ticket.prix_unitaire) {
            handleError(`Le prix promo doit être inférieur à ${formatXof(ticket.prix_unitaire)}.`);
            return;
        }

        setSavingPivotId(ticket.pivot_id);
        try {
            await axiosClient.post(
                `/dashboard/promotions/events/${id}/tickets/${ticket.pivot_id}`,
                {
                    promo_price: promoPrice,
                    starts_at: form.starts_at || null,
                    ends_at: form.ends_at || null,
                }
            );
            load();
        } catch (error) {
            handleError(error?.response?.data?.message || 'Activation impossible.');
        } finally {
            setSavingPivotId(null);
        }
    };

    const deactivatePromo = async (promotionId) => {
        setSavingPivotId(promotionId);
        try {
            await axiosClient.post(`/dashboard/promotions/${promotionId}/deactivate`);
            load();
        } catch (error) {
            handleError(error?.response?.data?.message || 'Désactivation impossible.');
        } finally {
            setSavingPivotId(null);
        }
    };

    if (loading) {
        return (
            <div className="ch3yp cnbwt cs7xl clu2m c6btv clbq0 cxsfz">
                <LoadContentSpinner />
            </div>
        );
    }

    if (!event) {
        return (
            <div className="ch3yp cnbwt cs7xl clu2m c6btv clbq0 cxsfz">
                <p className="text-gray-600 dark:text-gray-300">Événement introuvable.</p>
                <Link to="/dashboard/communiquer/promotions" className="text-primary-6000 text-sm mt-2 inline-block">
                    ← Retour aux promotions
                </Link>
            </div>
        );
    }

    return (
        <div className="ch3yp cnbwt cs7xl clu2m c6btv clbq0 cxsfz">
            <div className="mb-6">
                <Link
                    to="/dashboard/communiquer/promotions"
                    className="text-sm text-primary-6000 hover:underline"
                >
                    ← Promotions tarifaires
                </Link>
                <h1 className="text-gray-800 dark:text-gray-100 font-bold text-2xl mt-2">
                    {event.name}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {event.date || '—'} {event.hour ? `· ${event.hour}` : ''}
                    {isMultiSession && (
                        <span className="ml-2 inline-flex rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600 dark:text-slate-300">
                            Multi-sessions
                        </span>
                    )}
                </p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
                {/* Colonne gauche — paramétrage */}
                <div className="xl:col-span-7 space-y-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Sélectionnez un billet, définissez le nouveau prix et la période, puis activez.
                    </p>

                    {isMultiSession && (
                        <PassSessionTabs
                            event={event}
                            tickets={allTickets}
                            activeTab={activeTicketTab}
                            onChange={setActiveTicketTab}
                        />
                    )}

                    {visibleTickets.length === 0 && isMultiSession ? (
                        <p className="text-sm text-gray-500">Aucun billet pour cette sélection.</p>
                    ) : null}

                    {visibleTickets.map((ticket) => {
                        const form = forms[ticket.pivot_id] || {};
                        const active = ticket.promotion?.is_currently_active;
                        const isSelected = selectedPivotId === ticket.pivot_id;

                        return (
                            <div
                                key={ticket.pivot_id}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedPivotId(ticket.pivot_id)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedPivotId(ticket.pivot_id);
                                    }
                                }}
                                className={`rounded-xl border bg-white dark:bg-gray-800 p-4 md:p-5 transition ${
                                    isSelected
                                        ? 'border-primary-6000 ring-2 ring-primary-6000/20'
                                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                }`}
                            >
                                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                                    <div>
                                        <h2 className="font-semibold text-gray-800 dark:text-gray-100">
                                            {ticket.name}
                                        </h2>
                                        {isMultiSession && (
                                            <div className="mt-1">
                                                <SessionBadge ticket={ticket} />
                                            </div>
                                        )}
                                        <p className="text-sm text-gray-500">
                                            Catalogue : <strong>{formatXof(ticket.prix_unitaire)}</strong>
                                            {' · '}
                                            Stock : {ticket.nbr_ticket}
                                        </p>
                                    </div>
                                    {active ? (
                                        <span className="rounded-full bg-emerald-100 text-emerald-800 text-xs px-2 py-1 font-medium">
                                            Active
                                        </span>
                                    ) : (
                                        <span className="rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs px-2 py-1 font-medium">
                                            Inactive
                                        </span>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <label className="block text-sm" onClick={(e) => e.stopPropagation()}>
                                        <span className="text-gray-600 dark:text-gray-300">Nouveau prix (XOF)</span>
                                        <input
                                            type="number"
                                            min={MIN_PRICE}
                                            max={ticket.prix_unitaire - 1}
                                            value={form.promo_price}
                                            onChange={(e) =>
                                                updateForm(ticket.pivot_id, 'promo_price', e.target.value)
                                            }
                                            onFocus={() => setSelectedPivotId(ticket.pivot_id)}
                                            className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2"
                                            placeholder={`ex. ${Math.max(MIN_PRICE, ticket.prix_unitaire - 900)}`}
                                        />
                                    </label>
                                    <label className="block text-sm" onClick={(e) => e.stopPropagation()}>
                                        <span className="text-gray-600 dark:text-gray-300">Début (optionnel)</span>
                                        <input
                                            type="datetime-local"
                                            value={form.starts_at}
                                            onChange={(e) =>
                                                updateForm(ticket.pivot_id, 'starts_at', e.target.value)
                                            }
                                            onFocus={() => setSelectedPivotId(ticket.pivot_id)}
                                            className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2"
                                        />
                                    </label>
                                    <label className="block text-sm" onClick={(e) => e.stopPropagation()}>
                                        <span className="text-gray-600 dark:text-gray-300">Fin (optionnel)</span>
                                        <input
                                            type="datetime-local"
                                            value={form.ends_at}
                                            onChange={(e) =>
                                                updateForm(ticket.pivot_id, 'ends_at', e.target.value)
                                            }
                                            onFocus={() => setSelectedPivotId(ticket.pivot_id)}
                                            className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2"
                                        />
                                    </label>
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        type="button"
                                        disabled={savingPivotId === ticket.pivot_id}
                                        onClick={() => activatePromo(ticket)}
                                        className="btn bg-primary-6000 hover:bg-primary-700 text-white text-sm px-4 py-2 rounded-md disabled:opacity-50"
                                    >
                                        {active ? 'Mettre à jour la promo' : 'Activer la promotion'}
                                    </button>
                                    {ticket.promotion?.id && ticket.promotion?.is_active && (
                                        <button
                                            type="button"
                                            disabled={savingPivotId === ticket.promotion.id}
                                            onClick={() => deactivatePromo(ticket.promotion.id)}
                                            className="btn border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm px-4 py-2 rounded-md disabled:opacity-50"
                                        >
                                            Désactiver
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Colonne droite — récap + aperçu */}
                <aside className="xl:col-span-5 xl:sticky xl:top-6 space-y-4">
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                            Récapitulatif événement
                        </h3>
                        <dl className="space-y-2 text-sm">
                            <div className="flex justify-between gap-3">
                                <dt className="text-gray-500">Billets</dt>
                                <dd className="font-medium text-gray-800 dark:text-gray-100">{eventSummary.total}</dd>
                            </div>
                            <div className="flex justify-between gap-3">
                                <dt className="text-gray-500">Promos actives</dt>
                                <dd className="font-medium text-emerald-600">{eventSummary.activeCount}</dd>
                            </div>
                            <div className="flex justify-between gap-3">
                                <dt className="text-gray-500">Économie / billet (actives)</dt>
                                <dd className="font-medium text-gray-800 dark:text-gray-100">
                                    {formatXof(eventSummary.totalSavings)}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Aperçu acheteur
                            </h3>
                            {preview?.isActive && (
                                <span className="rounded-full bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 font-medium">
                                    En ligne
                                </span>
                            )}
                        </div>

                        {!preview ? (
                            <p className="text-sm text-gray-500">Sélectionnez un billet à gauche.</p>
                        ) : (
                            <>
                                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900/60 p-4 mb-4">
                                    <div className="flex items-end justify-between gap-3">
                                        <div>
                                            <div className="font-semibold text-gray-800 dark:text-gray-100">
                                                {preview.name}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {preview.stock} place(s) restante(s)
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            {preview.displayPromo != null && preview.discount ? (
                                                <>
                                                    <div className="text-sm text-gray-400 line-through">
                                                        {formatXof(preview.catalog)}
                                                    </div>
                                                    <div className="text-lg font-bold text-primary-6000">
                                                        {formatXof(preview.displayPromo)}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-lg font-bold text-gray-800 dark:text-gray-100">
                                                    {formatXof(preview.catalog)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {preview.discount && (
                                        <div className="mt-3 inline-flex rounded-md bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-xs font-medium px-2 py-1">
                                            −{formatXof(preview.discount.amount)} ({preview.discount.percent}&nbsp;%)
                                        </div>
                                    )}
                                </div>

                                <dl className="space-y-2 text-sm">
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-gray-500">Début</dt>
                                        <dd className="text-right text-gray-800 dark:text-gray-100">
                                            {selectedForm.starts_at || preview.starts_at
                                                ? formatDateTime(selectedForm.starts_at || preview.starts_at)
                                                : 'Dès activation'}
                                        </dd>
                                    </div>
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-gray-500">Fin</dt>
                                        <dd className="text-right text-gray-800 dark:text-gray-100">
                                            {selectedForm.ends_at || preview.ends_at
                                                ? formatDateTime(selectedForm.ends_at || preview.ends_at)
                                                : 'Sans date de fin'}
                                        </dd>
                                    </div>
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-gray-500">Brouillon valide</dt>
                                        <dd className={preview.validDraft ? 'text-emerald-600 font-medium' : 'text-gray-400'}>
                                            {preview.validDraft ? 'Oui — prêt à activer' : 'Non'}
                                        </dd>
                                    </div>
                                </dl>

                                <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                                    L’aperçu se met à jour en direct pendant que vous saisissez le prix et les dates.
                                    Le prix catalogue n’est jamais écrasé : seule la promotion affiche le nouveau tarif.
                                </p>
                            </>
                        )}
                    </div>
                </aside>
            </div>
        </div>
    );
}
